import { useState, type ReactNode } from 'react';
import {
  Button,
  makeStyles,
  mergeClasses,
  Text,
  Tooltip,
  tokens,
} from '@coreai/fluentui-react';
import {
  CheckmarkRegular,
  CopyRegular,
} from '@fluentui/react-icons';
import { JsonViewer } from '../ActionDetailPanel/JsonViewer';
import { RetryHistory } from '../ActionDetailPanel/RetryHistory';
import type { RequestTriggerContent, RetryAttempt, RequestContentSide, RequestContentRetryTarget } from '@/types/monitoring';
import { RequestContent } from '../shared/RequestContent';

/**
 * One column in the bottom property strip.
 *
 * `value` is a ReactNode so callers can colorize a status, render a chip,
 * or wrap a long token in a tooltip without the component needing to
 * know about any specific surface (action vs trigger).
 */
export type DetailProperty = {
  label: string;
  value: ReactNode;
  /** Optional native title (full timestamp, full id, etc). */
  title?: string | undefined;
  /** Extra className applied to the value Text — e.g. status colors. */
  valueClassName?: string | undefined;
  /**
   * When true, render a small copy button beside the value. Uses
   * `copyValue` if provided, otherwise falls back to `title`, then to
   * the value when it is a string.
   */
  copyable?: boolean | undefined;
  copyValue?: string | undefined;
};

type InputOutputDetailProps = {
  input: unknown;
  output: unknown;
  requestContent?: RequestTriggerContent | undefined;
  onRetryRequest?: ((side: RequestContentSide) => void) | undefined;
  retryingRequestSide?: RequestContentRetryTarget | undefined;
  /** Filename stem for the JsonViewer download buttons. */
  downloadStem: string;
  /** Cells rendered in the property strip across the bottom. */
  properties: DetailProperty[];
  /** Override the left pane label (default: "Input"). */
  inputLabel?: string;
  /** Override the right pane label (default: "Output"). */
  outputLabel?: string;
  /**
   * Optional size string appended next to the input pane label, e.g.
   * "1.4 KB". Rendered as a muted suffix; intentionally a string so the
   * formatting decision (binary vs decimal, decimals, locale) stays
   * with the caller.
   */
  inputSize?: string;
  outputSize?: string;
  /** Accessible label for the property strip region (default: "Details"). */
  ariaLabel?: string;
  /**
   * Retry attempts for the action. When non-empty, a bounded "Retry history"
   * section is rendered between the panes and the property strip. Triggers
   * never retry, so this is omitted for trigger detail.
   */
  retryHistory?: RetryAttempt[] | undefined;
};

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
  },
  panesRow: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
    minWidth: 0,
    // Stack vertically when the host is narrow. Side-by-side JSON only
    // makes sense once each pane has room to breathe; below ~720px the
    // viewer toolbar wraps and the diff between the two columns is
    // illegible.
    '@media (max-width: 720px)': {
      flexDirection: 'column',
    },
  },
  pane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    overflow: 'hidden',
  },
  paneRight: {
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (max-width: 720px)': {
      borderLeft: 'none',
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  propStrip: {
    flexShrink: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Bounded retry-history region between the panes and the property strip.
  // Capped so it never crowds out the JSON panes in the fullscreen dialog;
  // the inner field list owns its own scroll.
  retrySection: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '300px',
    minHeight: 0,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  propCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  propLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  propValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    minWidth: 0,
  },
  propValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  copyButton: {
    minWidth: '20px',
    width: '20px',
    height: '20px',
    padding: 0,
    flexShrink: 0,
  },
});

/**
 * Copy-to-clipboard mini-button used inside a property cell. Lives in
 * its own component so the parent strip doesn't re-render on every
 * "copied" tick — keeps the panes (which can hold large JSON) cheap to
 * update when the user clicks copy.
 */
const CopyCell = ({ value, label }: { value: string; label: string }) => {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip
      content={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}
      relationship="label"
      withArrow
    >
      <Button
        appearance="subtle"
        size="small"
        className={styles.copyButton}
        icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
        aria-label={`Copy ${label}`}
        onClick={() => {
          void navigator.clipboard
            .writeText(value)
            .then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => {
              // Clipboard may be unavailable; fail silently.
            });
        }}
      />
    </Tooltip>
  );
};

/**
 * Reusable side-by-side Input / Output viewer with a property strip
 * underneath. Used by `MonitoringExpandDialog` (for action runs) and by
 * `TriggerHistoryDetailPanel` (for trigger evaluations).
 *
 * The component owns the layout and the strip rendering only; the
 * underlying `JsonViewer` continues to own search, formatted/raw,
 * copy-all, and download.
 */
export const InputOutputDetail = ({
  input,
  output,
  requestContent,
  onRetryRequest,
  retryingRequestSide,
  downloadStem,
  properties,
  inputLabel = 'Input',
  outputLabel = 'Output',
  inputSize,
  outputSize,
  ariaLabel = 'Details',
  retryHistory,
}: InputOutputDetailProps) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.panesRow}>
        <div className={styles.pane}>
          {requestContent ? (
            <RequestContent content={requestContent.input} label="Input" onRetry={onRetryRequest} retryingSide={retryingRequestSide} />
          ) : <JsonViewer
            data={input}
            label={inputSize ? `${inputLabel} · ${inputSize}` : inputLabel}
            downloadName={`${downloadStem}-input`}
            maxHeight="100%"
          />}
        </div>
        <div className={mergeClasses(styles.pane, styles.paneRight)}>
          {requestContent ? (
            <RequestContent content={requestContent.output} label="Output" onRetry={onRetryRequest} retryingSide={retryingRequestSide} />
          ) : <JsonViewer
            data={output}
            label={outputSize ? `${outputLabel} · ${outputSize}` : outputLabel}
            downloadName={`${downloadStem}-output`}
            maxHeight="100%"
          />}
        </div>
      </div>
      {retryHistory && retryHistory.length > 0 && (
        <div
          className={styles.retrySection}
          role="region"
          aria-label="Retry history"
        >
          <RetryHistory attempts={retryHistory} />
        </div>
      )}
      <div
        className={styles.propStrip}
        role="region"
        aria-label={ariaLabel}
      >
        {properties.map((prop) => (
          <div key={prop.label} className={styles.propCell}>
            <Text className={styles.propLabel}>{prop.label}</Text>
            <div className={styles.propValueRow}>
              <Text
                className={mergeClasses(styles.propValue, prop.valueClassName)}
                title={prop.title}
              >
                {prop.value}
              </Text>
              {prop.copyable && (
                <CopyCell
                  value={prop.copyValue ?? prop.title ?? String(prop.value ?? '')}
                  label={prop.label}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
